import { Minus, Plus } from "lucide-react";
import { formatPrice } from "@/components/site/EventCard";

/**
 * Signature ticket-stub component:
 * left = ticket type info + price, right = quantity stepper.
 * Dashed perforation between the two halves using .svj-perf pseudo elements.
 *
 * Layout uses flex-wrap (not a viewport sm: breakpoint) so it adapts to the
 * ACTUAL width of whatever container it's rendered in — this card lives in a
 * narrow sidebar column, which can be under 640px wide even on a large
 * desktop screen, so a viewport-based breakpoint switches to the side-by-side
 * layout long before there's actually room for it.
 */
export default function TicketStub({
  type,
  qty,
  onChange,
  disabled,
  reasonDisabled,
  soldOut,
  // Timeslot-booking events ("Sinterklaas huisbezoeken"-style): the ticket
  // type's own price_cents is a meaningless placeholder here -- the real
  // price is decided per ticket unit at checkout, from whichever category/
  // timeslot the buyer picks. Showing "Gratis" (or any fixed amount) before
  // that choice is made is actively misleading, so this one caption/price
  // pair is swapped for neutral wording instead.
  timeslotBooking,
}) {
  const isFree = !timeslotBooking && (type.price_cents || 0) === 0;
  const price = timeslotBooking ? "Per afspraak" : isFree ? "Gratis" : formatPrice(type.price_cents);
  const remaining =
    type.capacity != null ? Math.max(0, type.capacity - (type.sold_count || 0)) : null;
  // Timeslot-booking events ("Sinterklaas huisbezoeken"-style): one order line
  // represents one appointment choice made during checkout -- more than one of
  // this ticket type per order would require picking several separate
  // appointments in a single line item, which the checkout form doesn't
  // support, so this is capped at 1 per order rather than the usual capacity.
  const maxQty = timeslotBooking ? 1 : remaining;

  const canIncrease =
    !disabled && !soldOut && (maxQty == null || qty < maxQty);
  const canDecrease = !disabled && qty > 0;

  return (
    <div
      data-testid={`ticket-stub-${type.id}`}
      className="relative flex flex-wrap bg-svj-card border border-svj-border rounded-2xl overflow-hidden shadow-lg shadow-black/40"
    >
      {/* Left — grows to fill available space, but never below 220px; wraps the
          stepper onto its own row automatically when there isn't room for both. */}
      <div className="flex-1 min-w-[220px] p-6 border-b-2 border-dashed border-svj-border relative svj-perf flex flex-col justify-center">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
              Ticket
            </div>
            <h3 className="mt-1 text-lg font-bold text-white">{type.name}</h3>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {soldOut && (
              <span className="rounded-full bg-black/60 border border-svj-border text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                Uitverkocht
              </span>
            )}
            {!soldOut && remaining != null && remaining <= 10 && remaining > 0 && (
              <span className="rounded-full bg-svj-orange/20 border border-svj-orange/50 text-svj-orange px-3 py-1 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                Nog {remaining}
              </span>
            )}
            <div className="text-right">
              <span className="text-xl font-extrabold text-svj-cyan whitespace-nowrap">{price}</span>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {timeslotBooking ? "Kies tijdens checkout" : isFree ? "Toegang" : "incl. btw"}
              </div>
              {type.pay_on_location && (
                <div className="text-[10px] uppercase tracking-widest text-svj-cyan/80" data-testid={`ticket-pay-on-location-${type.id}`}>
                  Betalen op locatie
                </div>
              )}
            </div>
          </div>
        </div>
        {type.description && (
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{type.description}</p>
        )}
        {reasonDisabled && (
          <div className="mt-3 text-xs text-slate-400" data-testid={`ticket-reason-${type.id}`}>
            {reasonDisabled}
          </div>
        )}
      </div>

      {/* Right — fixed natural width, never shrinks, wraps to its own row when
          the left side doesn't leave enough room. */}
      <div className="flex-shrink-0 w-full sm:w-auto p-6 flex flex-row items-center justify-center gap-3 bg-svj-card">
        <button
          type="button"
          onClick={() => canDecrease && onChange(qty - 1)}
          disabled={!canDecrease}
          data-testid={`ticket-decrease-${type.id}`}
          aria-label="Minder"
          className={`h-11 w-11 rounded-full border flex items-center justify-center transition-colors ${
            canDecrease
              ? "bg-svj-bg border-svj-border text-white hover:border-svj-cyan"
              : "bg-svj-bg/60 border-svj-border text-slate-600 cursor-not-allowed"
          }`}
        >
          <Minus className="h-5 w-5" />
        </button>
        <div
          className="min-w-[3ch] text-center text-2xl font-extrabold text-white"
          data-testid={`ticket-qty-${type.id}`}
        >
          {qty}
        </div>
        <button
          type="button"
          onClick={() => canIncrease && onChange(qty + 1)}
          disabled={!canIncrease}
          data-testid={`ticket-increase-${type.id}`}
          aria-label="Meer"
          className={`h-11 w-11 rounded-full flex items-center justify-center transition-transform ${
            canIncrease
              ? "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:-translate-y-0.5"
              : "bg-svj-bg/60 border border-svj-border text-slate-600 cursor-not-allowed"
          }`}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
